/**
 * Editor top bar (Paper "Experiments · 02 Editor"): back, the test name over
 * "page · save state", the version switcher, device, undo/redo and Launch test.
 */
import { useEffect, useState } from "react"
import { cn } from "@/lib/utils"
import type { VariantId } from "@/lib/contexts/EditorModeContext"

type VersionTab = { id: VariantId; name: string; instructionCount: number }

type EditorTopBarProps = {
  name: string
  onRename: (name: string) => void
  pageLabel: string
  saveState: "saved" | "unsaved" | "saving" | "elsewhere"
  versions: VersionTab[]
  currentVersion: VariantId
  onSwitchVersion: (id: VariantId) => void
  device: "desktop" | "mobile" | "fit"
  onDevice: (device: "desktop" | "mobile") => void
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  onBack: () => void
  /** The Launch tab is open: the bar's button steps back so the panel's orange one leads */
  launchTabOpen: boolean
  onLaunchClick: () => void
}

const SAVE_LABEL = { saved: "Draft saved", unsaved: "Unsaved changes", saving: "Saving…", elsewhere: "Changed in another tab" } as const

export function EditorTopBar({
  name,
  onRename,
  pageLabel,
  saveState,
  versions,
  currentVersion,
  onSwitchVersion,
  device,
  onDevice,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onBack,
  launchTabOpen,
  onLaunchClick,
}: EditorTopBarProps) {
  const [draftName, setDraftName] = useState(name)
  useEffect(() => setDraftName(name), [name])

  return (
    <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background pl-3.5 pr-3">
      <button
        type="button"
        onClick={onBack}
        aria-label="Back"
        className="flex size-[34px] shrink-0 items-center justify-center rounded-lg text-ui-ink ring-1 ring-black/[0.08] transition-colors hover:bg-muted dark:ring-white/10"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden><path d="m15 18-6-6 6-6" fill="none" stroke="currentColor" strokeWidth="2.14" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <div className="flex min-w-0 flex-col gap-px">
        <input
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onBlur={() => {
            const next = draftName.trim()
            if (next && next !== name) onRename(next)
            else setDraftName(name)
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur()
          }}
          aria-label="Test name"
          className="w-[260px] max-w-[30vw] truncate rounded-[5px] bg-transparent text-sm font-semibold leading-[18px] tracking-[-0.01em] text-ui-title outline-none hover:bg-muted/60 focus:bg-muted/60 focus:ring-1 focus:ring-ring"
        />
        <span className="truncate text-xs leading-4 text-foreground/60">
          {pageLabel} · <span className={cn((saveState === "unsaved" || saveState === "elsewhere") && "text-warning")}>{SAVE_LABEL[saveState]}</span>
        </span>
      </div>

      <span className="grow basis-0" />

      <div className="flex gap-0.5 rounded-lg bg-muted p-0.5" role="tablist" aria-label="Version">
        {versions.map((v) => {
          const active = v.id === currentVersion
          const isControl = v.id === "A"
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onSwitchVersion(v.id)}
              title={isControl ? "Control is the page as it is today" : `Edit ${v.name}`}
              className={cn(
                "flex h-7 items-center gap-1.5 rounded-[6px] px-2.5 transition-colors",
                active ? "bg-background shadow-[0_1px_2px_rgb(0_0_0/0.08),0_0_0_1px_rgb(0_0_0/0.04)]" : "hover:bg-background/60",
              )}
            >
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded-[4px] text-xs font-semibold leading-[14px]",
                  isControl ? "bg-[#BDBDBD] text-ui-title dark:bg-white/25" : active ? "bg-primary text-primary-foreground" : "bg-foreground/15 text-ui-title",
                )}
              >
                {v.id}
              </span>
              <span className={cn("text-xs font-medium leading-4", active ? "text-ui-title" : "text-foreground/60")}>
                {isControl ? "Control" : v.name}
              </span>
              {!isControl && v.instructionCount > 0 && (
                <span className="text-xs leading-4 text-foreground/60">
                  {v.instructionCount} {v.instructionCount === 1 ? "change" : "changes"}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <span className="grow basis-0" />

      <div className="flex items-center gap-0.5">
        <IconButton label="Undo" disabled={!canUndo} onClick={onUndo}>
          <path d="M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </IconButton>
        <IconButton label="Redo" disabled={!canRedo} onClick={onRedo}>
          <path d="m15 14 5-5-5-5M20 9H9a5 5 0 0 0 0 10h3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </IconButton>
      </div>

      <div className="flex items-center gap-0.5 rounded-lg bg-muted p-0.5" role="tablist" aria-label="Device">
        <DeviceButton label="Desktop" active={device !== "mobile"} onClick={() => onDevice("desktop")}>
          <rect x="3" y="4" width="18" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M8 20h8M12 16v4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </DeviceButton>
        <DeviceButton label="Mobile" active={device === "mobile"} onClick={() => onDevice("mobile")}>
          <rect x="7" y="2" width="10" height="20" rx="2.5" fill="none" stroke="currentColor" strokeWidth="2" />
        </DeviceButton>
      </div>

      <span className="h-5 w-px shrink-0 bg-border" />

      <button
        type="button"
        onClick={onLaunchClick}
        className={cn(
          "flex h-[34px] items-center rounded-lg px-3.5 text-[13px] font-medium transition-colors",
          launchTabOpen
            ? "bg-background text-ui-ink ring-1 ring-black/10 hover:bg-muted dark:ring-white/10"
            : "bg-primary text-primary-foreground hover:opacity-90",
        )}
      >
        Launch test
      </button>
    </div>
  )
}

function IconButton({ label, disabled, onClick, children }: { label: string; disabled: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-[30px] items-center justify-center rounded-[6px] text-ui-ink transition-colors hover:bg-muted disabled:text-ui-meta disabled:hover:bg-transparent"
    >
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>{children}</svg>
    </button>
  )
}

function DeviceButton({ label, active, onClick, children }: { label: string; active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn(
        "flex h-7 w-[30px] items-center justify-center rounded-[6px] transition-colors",
        active ? "bg-background text-ui-ink shadow-[0_1px_2px_rgb(0_0_0/0.08)]" : "text-ui-meta hover:text-ui-ink",
      )}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>{children}</svg>
    </button>
  )
}

export default EditorTopBar
